import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChangePasswordPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Las contrasenas no coinciden'); return; }
    if (password.length < 6) { toast.error('Minimo 6 caracteres'); return; }

    setLoading(true);
    try {
      const token = localStorage.getItem('ofimex_token');
      await axios.post(`${API}/api/auth/change-password`, { new_password: password }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update user in localStorage to remove must_change_password
      const stored = JSON.parse(localStorage.getItem('ofimex_user') || '{}');
      stored.must_change_password = false;
      localStorage.setItem('ofimex_user', JSON.stringify(stored));
      toast.success('Contrasena actualizada');
      // Redirect based on role
      if (stored.is_super_admin || stored.is_master_admin) navigate('/super-admin');
      else if (stored.role === 'client') navigate('/my-dashboard');
      else navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-lg p-8 shadow-xl">
        <div className="text-center mb-8">
          <img src="/logo-ofimex-white.png" alt="OFIMEX" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold">Cambiar Contrasena</h1>
          <p className="text-muted-foreground text-sm mt-2">
            {user?.must_change_password
              ? 'Debes crear una nueva contrasena para continuar'
              : 'Actualiza tu contrasena'}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label>Nueva Contrasena</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type={show ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimo 6 caracteres"
                className="pl-10 pr-10"
                required
                data-testid="new-password-input"
              />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <Label>Confirmar Contrasena</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repite la contrasena"
                className="pl-10"
                required
                data-testid="confirm-password-input"
              />
            </div>
            {confirm && password !== confirm && (
              <p className="text-xs text-red-500 mt-1">Las contrasenas no coinciden</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={loading || !password || !confirm || password !== confirm} data-testid="change-password-btn">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</> : 'Guardar Contrasena'}
          </Button>
        </form>
      </div>
    </div>
  );
}
